/**
 * Responsive application shell. A sticky top bar carries the product name, the
 * primary nav (Home · Flows · Active · History · Memories, plus Access for a
 * company's administrators and Tenants for EVT platform administrators), the
 * active company (a switcher when the person belongs to several),
 * and a sign-out control; the routed page renders beneath in a centered column.
 *
 * The nav collapses behind a hamburger below ~720px (CSS-driven via the
 * `.portal-nav` / `.portal-nav-toggle` rules in portal.css). Sign-out calls the
 * BFF logout then hard-navigates to /login so all client cache is dropped.
 */
import { Icon, Spinner } from '@evt/flow-stream-ui'
import { type ReactNode, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { logout } from '../api/auth'
import { useSession } from '../auth/useSession'
import { CompanySwitcher } from '../components/CompanySwitcher'
import { LanguageSwitcher, useI18n } from '../i18n'

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useI18n()
  const { contractor, contractors, user, permissions, platformAdmin } = useSession()
  const canManageAccess = permissions.includes('access:manage')
  const [navOpen, setNavOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const onSignOut = async () => {
    setSigningOut(true)
    let redirectTo = '/login'
    try {
      // RP-initiated logout: the BFF may hand back an IdP end-session URL to visit.
      const res = await logout()
      if (res?.redirectTo) redirectTo = res.redirectTo
    } catch {
      // Even if logout fails server-side, drop the user to /login.
    }
    location.assign(redirectTo)
  }

  const closeNav = () => setNavOpen(false)

  return (
    <div className="portal-shell">
      <header className="portal-topbar">
        <NavLink to="/" className="portal-brand" onClick={closeNav}>
          <Icon name="bolt" size={18} />
          <span>Smith Factory</span>
        </NavLink>

        <button
          type="button"
          className="portal-nav-toggle btn btn-ghost btn-icon"
          aria-label={t.shell.toggleNavigation}
          aria-expanded={navOpen}
          onClick={() => setNavOpen((v) => !v)}
        >
          <Icon name={navOpen ? 'x' : 'menu'} size={18} />
        </button>

        <nav className={`portal-nav${navOpen ? ' open' : ''}`}>
          <NavLink to="/" end className={({ isActive }) => (isActive ? 'on' : '')} onClick={closeNav}>
            {t.shell.navHome}
          </NavLink>
          <NavLink to="/flows" className={({ isActive }) => (isActive ? 'on' : '')} onClick={closeNav}>
            {t.shell.navFlows}
          </NavLink>
          <NavLink to="/active" className={({ isActive }) => (isActive ? 'on' : '')} onClick={closeNav}>
            {t.shell.navActive}
          </NavLink>
          <NavLink to="/conversations" className={({ isActive }) => (isActive ? 'on' : '')} onClick={closeNav}>
            {t.shell.navHistory}
          </NavLink>
          <NavLink to="/memories" className={({ isActive }) => (isActive ? 'on' : '')} onClick={closeNav}>
            {t.shell.navMemories}
          </NavLink>
          {canManageAccess && (
            <NavLink to="/access" className={({ isActive }) => (isActive ? 'on' : '')} onClick={closeNav}>
              {t.shell.navAccess}
            </NavLink>
          )}
          {platformAdmin && (
            <NavLink to="/platform/tenants" className={({ isActive }) => (isActive ? 'on' : '')} onClick={closeNav}>
              {t.shell.navTenants}
            </NavLink>
          )}

          <div className="portal-nav-meta">
            <CompanySwitcher contractor={contractor} contractors={contractors} />
            {user && <span className="portal-user muted">{user.email}</span>}
            <LanguageSwitcher />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={signingOut}
              onClick={() => void onSignOut()}
            >
              {signingOut ? <Spinner size={13} /> : <Icon name="lock" size={13} />} {t.shell.signOut}
            </button>
          </div>
        </nav>
      </header>

      <main className="portal-main">{children}</main>
    </div>
  )
}
